import { asGqlData, asWidgetProps } from 'Src/types/widget-helpers';
import React, { ComponentProps, useEffect } from 'react';
import { b2cPagesService } from '@clubmed/utils';
import { ToasterImmersive } from 'Components/frameworks/toaster-immersive/ToasterImmersive';
import { ICONS } from 'Components/utils/icon/icons';
import { usePageContext } from 'Context/PageContext';
import { useAppContext } from 'Context/ReactAppContext';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { useAuthentication } from 'Src/hooks/use-authentication/useAuthentication';
import { t } from 'Src/i18n';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { isAnalyticsEnabled } from 'Utils/analytics/analyticsUtils';
import { API_AVAILABLE } from 'Utils/api-status/constants';
import { formatVoucherValue } from 'Components/widgets/ftc/ftc.utils';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { useGQL } from 'Utils/graphql-client/use-gql/useGQL';
import { CUSTOMER_ACCOUNT_GET_VOUCHERS } from 'Utils/request/requestDictionary';
type VouchersType = {
  items: Array<VoucherEntity>;
  type: string;
};
const voucherQuery = `query {
    vouchers {
      type
      items {
        value
        unit
        currency
        id
        isExpired
        originType
        status
      }
    }
 }`;
export const FTCToasterContainer = ({
  onClose,
  setIsVisible,
}: {
  onClose?: () => void;
  setIsVisible?: (arg0: boolean) => void;
}) => {
  const appContext = useAppContext();
  const { pageType } = usePageContext();
  const { isAppMounted } = useResponsive();
  const track = useTracking();
  const { PAGE_TYPES } = b2cPagesService;
  const isAllowedPage = [PAGE_TYPES.RESORT, PAGE_TYPES.CRUISE].includes(pageType);
  const displayFTC = isFeatureFlipped(appContext)('booking.displayFutureTravelCredit');
  const displayFTCToShopping = isFeatureFlipped(appContext)('shopping.displayFutureTravelCredit');
  const { isLogged } = useAuthentication();
  const { data, isLoading } = useGQL({
    query: voucherQuery,
    queryOptions: {
      requestName: CUSTOMER_ACCOUNT_GET_VOUCHERS,
    },
    cache: false,
    ignore: !isLogged || !isAllowedPage || !displayFTC,
  });
  const gqlData = asGqlData<{ vouchers?: VouchersType[] }>(data);
  const creditNotesVouchers: VouchersType | null | undefined = gqlData?.vouchers?.find(
    ({ type }) => type === 'CREDIT_NOTE',
  );
  const creditNoteVoucher: VoucherEntity | null | undefined = creditNotesVouchers?.items.find(
    (voucher: VoucherEntity) =>
      voucher.status === API_AVAILABLE && voucher.originType.startsWith('<DOS='),
  );
  const hasCreditNoteVoucher = !!creditNoteVoucher;
  useEffect(() => {
    if (hasCreditNoteVoucher) {
      track({
        actionGA: 'FTC',
        labelGA: 'displayAmountToaster',
      });
    }
  }, [hasCreditNoteVoucher]);
  const title = t(
    hasCreditNoteVoucher
      ? 'resort.futureTravelCredit.specificTitle'
      : 'resort.futureTravelCredit.genericTitle',
  );
  const text = t(
    hasCreditNoteVoucher
      ? 'resort.futureTravelCredit.message'
      : 'resort.futureTravelCredit.genericDescription',
  );
  const subtitle = creditNoteVoucher && formatVoucherValue(creditNoteVoucher, appContext.locale);
  const shouldSkipRendering =
    !isAppMounted ||
    !isAllowedPage ||
    !displayFTC ||
    (isLoading && isLogged) ||
    (!isLoading && isLogged && !hasCreditNoteVoucher) ||
    (!displayFTCToShopping && !isLogged) ||
    !isAnalyticsEnabled();
  useEffect(() => {
    setIsVisible && setIsVisible(!shouldSkipRendering);
  }, [shouldSkipRendering]);
  return (
    !shouldSkipRendering && (
      <ToasterImmersive
        {...asWidgetProps<ComponentProps<typeof ToasterImmersive>>({
          title: title,
          text: text,
          subtitle: subtitle,
          iconName: ICONS.PROMO_CODE,
          onClose: () => {
            onClose && onClose();
          },
        })}
      />
    )
  );
};
